import { useState } from 'react';
import { saveNotificationPreferences } from '../../services/supabaseClient.js';

// ─────────────────────────────────────────────────────────────────
// SetRemindersModal — study-reminder preference capture. Persists the
// choice to learners.notification_preferences; does NOT send anything
// (delivery is a future integration).
// ─────────────────────────────────────────────────────────────────

const AMBER = 'var(--brand-amber)';
const SLATE = 'var(--brand-slate)';
const INK   = '#1e1e2e';

const FREQUENCIES = ['Daily', 'Weekdays only', 'Weekly', 'Custom time'];
const TIMES = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '12:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM'];

export default function SetRemindersModal({ learner, onClose, onSaved }) {
  const [channel,   setChannel]   = useState('email');           // 'email' | 'phone'
  const [email,     setEmail]     = useState(learner?.email || '');
  const [phone,     setPhone]     = useState(learner?.phone || '');
  const [frequency, setFrequency] = useState('Daily');
  const [time,      setTime]      = useState('8:00 AM');
  const [agreed,    setAgreed]    = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);

  const contact = channel === 'email' ? email : phone;
  const canSave = !!channel && !!contact.trim() && agreed && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    const ok = await saveNotificationPreferences(learner?.id, {
      channel,
      contact: contact.trim(),
      frequency,
      time,
      agreed_at: new Date().toISOString(),
    });
    setSaving(false);
    if (ok) onSaved?.();
    else setError('Could not save your reminders. Please try again.');
  };

  const pill = (active) => ({
    flex: 1, padding: '9px 12px', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit', textAlign: 'center',
    border: `1px solid ${active ? AMBER : '#e5e7eb'}`,
    background: active ? AMBER : '#fff',
    color: active ? '#fff' : SLATE,
  });
  const chip = (active) => ({
    padding: '7px 13px', borderRadius: 99, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
    border: `1px solid ${active ? AMBER : '#e5e7eb'}`,
    background: active ? '#fffaf0' : '#fff',
    color: active ? AMBER : SLATE,
  });
  const label = { fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: SLATE, margin: '0 0 8px' };
  const input = {
    width: '100%', padding: '10px 13px', borderRadius: 8, fontSize: 13,
    border: '1px solid #e5e7eb', background: '#f8fafc', color: INK, fontFamily: 'inherit', outline: 'none',
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: '28px 30px', position: 'relative', boxShadow: '0 12px 48px rgba(0,0,0,0.25)' }}>
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 16, right: 16, width: 30, height: 30, borderRadius: '50%', border: '1px solid #e5e7eb', background: '#fff', color: SLATE, fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>

        {/* Header */}
        <h2 style={{ fontSize: 20, fontWeight: 800, color: INK, margin: '0 0 5px' }}>🔔 Set Study Reminders</h2>
        <p style={{ fontSize: 13, color: SLATE, margin: '0 0 22px' }}>Stay on track with reminders that work for you.</p>

        {/* Channel */}
        <p style={label}>Send reminders to</p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <button type="button" onClick={() => setChannel('email')} style={pill(channel === 'email')}>📧 Email</button>
          <button type="button" onClick={() => setChannel('phone')} style={pill(channel === 'phone')}>📱 Phone</button>
        </div>
        {channel === 'email' ? (
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" style={{ ...input, marginBottom: 22 }} />
        ) : (
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" style={{ ...input, marginBottom: 22 }} />
        )}

        {/* Frequency */}
        <p style={label}>How often</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          {FREQUENCIES.map(f => (
            <button key={f} type="button" onClick={() => setFrequency(f)} style={chip(frequency === f)}>{f}</button>
          ))}
        </div>

        {/* Time */}
        <p style={label}>Best time</p>
        <select value={time} onChange={e => setTime(e.target.value)} style={{ ...input, width: 'auto', minWidth: 140, cursor: 'pointer', marginBottom: 22 }}>
          {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Agreement */}
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 22 }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ width: 16, height: 16, marginTop: 1, accentColor: '#d97706', cursor: 'pointer', flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: SLATE, lineHeight: 1.5 }}>
            I agree to receive study reminders from AdaptOS. You can unsubscribe anytime in Settings.
          </span>
        </label>

        {error && <p style={{ fontSize: 12.5, color: 'var(--brand-coral)', margin: '0 0 12px' }}>{error}</p>}

        <button type="button" onClick={handleSave} disabled={!canSave}
          style={{
            width: '100%', padding: '13px', borderRadius: 10, background: AMBER, border: 'none',
            color: '#fff', fontSize: 14, fontWeight: 800, fontFamily: 'inherit',
            cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.5,
            boxShadow: canSave ? '0 4px 14px rgba(217,119,6,0.28)' : 'none',
          }}>
          {saving ? 'Saving…' : 'Save Reminders →'}
        </button>
      </div>
    </div>
  );
}
